import type { Header } from '@tanstack/react-table';

export function getHeaderLeafIds<T>(header: Header<T, unknown>, leafIdSet: Set<string>) {
	return header
		.getLeafHeaders()
		.map((leafHeader) => leafHeader.column.id)
		.filter((leafId) => leafIdSet.has(leafId));
}

export function getFirstRenderedDescendantRow<T>(
	header: Header<T, unknown>,
	rowByHeaderId: Record<string, number>,
	maxDepth: number
): number {
	const children = header.subHeaders.filter((child) => !child.isPlaceholder);

	if (!children.length) {
		return maxDepth;
	}

	let minRow = maxDepth;

	for (const child of children) {
		const childRow = rowByHeaderId[child.id] ?? (child.depth + 1);
		minRow = Math.min(minRow, childRow);

		if (child.subHeaders.length > 0) {
			minRow = Math.min(minRow, getFirstRenderedDescendantRow(child, rowByHeaderId, maxDepth));
		}
	}

	return minRow;
}

export function getHeaderRowRange<T>(
	header: Header<T, unknown>,
	rowByHeaderId: Record<string, number>,
	maxDepth: number
) {
	const hasSubHeaders = header.subHeaders.length > 0;

	let rowStart: number;
	let rowEnd: number;

	if (!hasSubHeaders) {
		rowStart = maxDepth;
		rowEnd = maxDepth + 1;
	} else {
		rowStart = rowByHeaderId[header.id] ?? (header.depth + 1);

		const firstRenderedDescendantRow = getFirstRenderedDescendantRow(header, rowByHeaderId, maxDepth);

		rowEnd = Math.max(rowStart + 1, firstRenderedDescendantRow);

		// safety: group header should never be rendered on the last row
		rowEnd = Math.min(rowEnd, maxDepth);
	}

	return {
		hasSubHeaders,
		rowStart,
		rowEnd,
	};
}

export function getSimpleHeaderColumnRange<T>(header: Header<T, unknown>, leafIdIndex: Map<string, number>) {
	const leafHeaders = header.getLeafHeaders();
	const firstLeafId = leafHeaders[0]?.column.id;
	const lastLeafId = leafHeaders[leafHeaders.length - 1]?.column.id;

	if (!firstLeafId || !lastLeafId) {
		return null;
	}

	const firstIndex = leafIdIndex.get(firstLeafId);
	const lastIndex = leafIdIndex.get(lastLeafId);

	if (firstIndex === undefined || lastIndex === undefined) {
		return null;
	}

	return {
		columnStart: firstIndex + 1,
		columnEnd: lastIndex + 1,
	};
}
